/**
 * Single source of truth for cookie/analytics consent.
 *
 * DSGVO requires opt-in for non-essential trackers (analytics, session replay).
 * Until a user explicitly accepts, all trackers must stay disabled.
 *
 * Storage shape:
 *   localStorage["cookie-consent"] = JSON.stringify({
 *     necessary: true,           // always true, can't be opted out
 *     analytics: boolean,        // PostHog product analytics + session replay
 *     marketing: boolean,        // ads pixels (none today, reserved)
 *     decidedAt: ISO timestamp,
 *   })
 *
 * If the key is missing the user has NOT decided yet — show the banner,
 * keep all non-essential trackers off.
 */

export type ConsentCategories = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
};

export type ConsentState = ConsentCategories & {
  decidedAt: string;
};

const STORAGE_KEY = 'cookie-consent';
const EVENT_NAME = 'eb:consent-change';

export function getConsent(): ConsentState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    return {
      necessary: true,
      analytics: !!parsed.analytics,
      marketing: !!parsed.marketing,
      decidedAt: parsed.decidedAt || new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

export function hasDecided(): boolean {
  return getConsent() !== null;
}

export function hasAnalyticsConsent(): boolean {
  return getConsent()?.analytics === true;
}

export function setConsent(input: Partial<ConsentCategories>): ConsentState {
  const state: ConsentState = {
    necessary: true,
    analytics: !!input.analytics,
    marketing: !!input.marketing,
    decidedAt: new Date().toISOString(),
  };
  if (typeof window !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    window.dispatchEvent(new CustomEvent<ConsentState>(EVENT_NAME, { detail: state }));
  }
  return state;
}

export function acceptAll(): ConsentState {
  return setConsent({ analytics: true, marketing: true });
}

export function acceptNecessary(): ConsentState {
  return setConsent({ analytics: false, marketing: false });
}

export function revokeConsent(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: null }));
}

export function onConsentChange(
  cb: (state: ConsentState | null) => void
): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const handler = (e: Event) => {
    const detail = (e as CustomEvent<ConsentState | null>).detail;
    cb(detail ?? getConsent());
  };
  window.addEventListener(EVENT_NAME, handler);
  return () => window.removeEventListener(EVENT_NAME, handler);
}
